(() => {
  const instances = new Map();
  let documentObserver = null;
  let restoreFrame = null;

  function removeLegacyHumm(productForm) {
    productForm
      .querySelectorAll(":scope > #flexifi-tag.flexifi-widget, :scope > .flexifi-widget")
      .forEach((element) => element.remove());
  }

  function restoreFinanceOptions() {
    restoreFrame = null;

    instances.forEach((financeOptions, productId) => {
      const productRoot = document.querySelector(
        '#main-product[data-form-id="main-product-form-' + CSS.escape(productId) + '"]'
      );

      if (!productRoot) return;

      const productForm = productRoot.querySelector(".f8pr");

      if (!productForm) return;

      removeLegacyHumm(productForm);

      if (financeOptions.isConnected) return;

      const variantSelection = productForm.querySelector(
        ".f8pr-variant-selection"
      );

      const price = productForm.querySelector(".f8pr-price");

      if (variantSelection) {
        variantSelection.insertAdjacentElement("afterend", financeOptions);
      } else if (price) {
        price.insertAdjacentElement("afterend", financeOptions);
      } else {
        productForm.prepend(financeOptions);
      }
    });
  }

  function scheduleRestore() {
    if (restoreFrame) return;

    restoreFrame = window.requestAnimationFrame(restoreFinanceOptions);
  }

  function observeProductPage() {
    if (documentObserver) return;

    const observationRoot =
      document.getElementById("content") || document.body;

    documentObserver = new MutationObserver(scheduleRestore);
    documentObserver.observe(observationRoot, {
      childList: true,
      subtree: true
    });
  }

  class BTFinanceOptions extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.priceElement = this.querySelector("[data-finance-price]");
      this.variantsElement = this.querySelector("[data-finance-variants]");
      this.productRoot = this.closest("#main-product");

      if (!this.productRoot) return;

      instances.set(this.dataset.productId, this);
      observeProductPage();

      const productFormContainer = this.closest(".f8pr");

      if (productFormContainer) {
        removeLegacyHumm(productFormContainer);
      }

      if (!this.priceElement || !this.variantsElement) return;

      try {
        this.variants = JSON.parse(this.variantsElement.textContent);
      } catch {
        this.variants = [];
      }

      if (!Array.isArray(this.variants) || !this.variants.length) return;

      const formId = this.productRoot.dataset.formId;

      this.productForm = formId
        ? document.getElementById(formId)
        : this.productRoot.querySelector('form[action*="/cart/add"]');

      this.handleProductChange = this.handleVariantChange.bind(this);
      this.handleHistoryChange = () => {
        this.selectedVariantId = null;
        this.scheduleUpdate();
      };

      this.productRoot.addEventListener(
        "change",
        this.handleProductChange,
        true
      );

      window.addEventListener("popstate", this.handleHistoryChange);

      this.variantState = this.productRoot.querySelector(
        "[data-current-variant]"
      );

      if (this.variantState) {
        this.variantObserver = new MutationObserver(() => {
          this.scheduleUpdate();
        });

        this.variantObserver.observe(this.variantState, {
          attributes: true,
          attributeFilter: ["data-current-variant"]
        });
      }

      this.update(true);
    }

    disconnectedCallback() {
      this.productRoot?.removeEventListener(
        "change",
        this.handleProductChange,
        true
      );

      window.removeEventListener(
        "popstate",
        this.handleHistoryChange
      );

      this.variantObserver?.disconnect();
      window.clearTimeout(this.klarnaUpdateTimer);

      if (this.updateFrame) {
        window.cancelAnimationFrame(this.updateFrame);
      }

      this.initialized = false;
      scheduleRestore();
    }

    handleVariantChange(event) {
      const target = event.target;

      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement
      ) {
        if (target.name === "variant-id" || target.name === "id") {
          const variantId = Number(target.value);

          if (variantId) {
            this.selectedVariantId = variantId;
          }
        }
      }

      this.scheduleUpdate();
    }

    scheduleUpdate() {
      if (this.updateFrame) {
        window.cancelAnimationFrame(this.updateFrame);
      }

      this.updateFrame = window.requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    }

    getVariantId() {
      if (this.selectedVariantId) {
        return this.selectedVariantId;
      }

      const checkedVariant = this.productRoot.querySelector(
        'input[name="variant-id"]:checked'
      );

      if (checkedVariant?.value) {
        return Number(checkedVariant.value);
      }

      const selectedVariant = this.productRoot.querySelector(
        'select[name="variant-id"]'
      );

      if (selectedVariant?.value) {
        return Number(selectedVariant.value);
      }

      const formVariant = this.productForm
        ?.querySelector('[name="id"]')
        ?.value;

      if (formVariant) {
        return Number(formVariant);
      }

      const currentVariant = this.productRoot
        .querySelector("[data-current-variant]")
        ?.dataset.currentVariant;

      if (currentVariant) {
        return Number(currentVariant);
      }

      const urlVariant = new URL(window.location.href)
        .searchParams
        .get("variant");

      return urlVariant ? Number(urlVariant) : null;
    }

    getSelectedVariant() {
      const variantId = this.getVariantId();

      if (!variantId) return this.variants[0] || null;

      return (
        this.variants.find((variant) => variant.id === variantId) ||
        null
      );
    }

    update(force = false) {
      const variant = this.getSelectedVariant();

      if (!variant || typeof variant.price !== "number") return;

      if (!force && this.currentVariantId === variant.id) return;

      this.currentVariantId = variant.id;
      this.dataset.variantId = String(variant.id);

      const hummPrice = (variant.price / 100).toFixed(2);

      if (this.priceElement.textContent.trim() !== hummPrice) {
        this.priceElement.textContent = hummPrice;
      }

      this.updateHumm(variant.price);
      this.updateKlarna(variant.price);
    }

    updateHumm(purchaseAmount) {
      const widget = this.querySelector("[data-humm-widget]");
      const merchantId =
        widget?.dataset.hummMerchantId ||
        this.dataset.hummMerchantId;

      if (!widget || !merchantId) return;

      const productPrice = (purchaseAmount / 100).toFixed(2);

      if (
        widget.dataset.hummPrice === productPrice &&
        widget.querySelector("#flexifi-tag")
      ) {
        return;
      }

      const requestId = String(
        Number(widget.dataset.hummRequestId || 0) + 1
      );

      widget.dataset.hummRequestId = requestId;
      widget.dataset.hummPrice = productPrice;

      widget
        .querySelectorAll(
          "[data-humm-frame], script[src*='flexifi-widget.js']"
        )
        .forEach((element) => element.remove());

      const frame = document.createElement("iframe");

      frame.hidden = true;
      frame.tabIndex = -1;
      frame.setAttribute("aria-hidden", "true");
      frame.setAttribute("data-humm-frame", "");

      const renderStartedAt = Date.now();

      const collectWidget = () => {
        if (widget.dataset.hummRequestId !== requestId) {
          frame.remove();
          return;
        }

        const renderedWidget =
          frame.contentDocument?.querySelector("#flexifi-tag");

        if (renderedWidget) {
          const officialWidget =
            document.adoptNode(renderedWidget);

          widget
            .querySelectorAll("#flexifi-tag, .flexifi-widget")
            .forEach((element) => element.remove());

          widget.appendChild(officialWidget);
          frame.remove();
          return;
        }

        if (Date.now() - renderStartedAt < 5000) {
          window.setTimeout(collectWidget, 100);
          return;
        }

        frame.remove();
      };

      const widgetUrl =
        "https://d3v2ir16k1una.cloudfront.net/content/scripts/" +
        "flexifi-widget.js?id=" +
        encodeURIComponent(merchantId) +
        "&productPrice=" +
        encodeURIComponent(productPrice);

      frame.srcdoc =
        "<!doctype html><html><head><meta charset='utf-8'></head>" +
        "<body><script src='" +
        widgetUrl.replaceAll("&", "&amp;") +
        "'><\/script></body></html>";

      widget.appendChild(frame);
      window.setTimeout(collectWidget, 100);
    }

    updateKlarna(purchaseAmount) {
      const purchaseAmountValue = String(purchaseAmount);

      this.klarnaPurchaseAmount = purchaseAmount;
      window.clearTimeout(this.klarnaUpdateTimer);

      const synchronizePlacement = () => {
        if (this.klarnaPurchaseAmount !== purchaseAmount) return;

        const placements =
          this.querySelectorAll("klarna-placement");

        if (!placements.length) return;

        placements.forEach((placement) => {
          placement.dataset.purchaseAmount =
            purchaseAmountValue;
        });

        if (
          window.KOSMApp &&
          typeof window.KOSMApp.updatePurchaseAmount ===
            "function"
        ) {
          window.KOSMApp.updatePurchaseAmount(
            purchaseAmount
          );
        }

        if (
          window.Klarna?.OnsiteMessaging &&
          typeof window.Klarna.OnsiteMessaging.refresh ===
            "function"
        ) {
          window.Klarna.OnsiteMessaging.refresh();
        }
      };

      synchronizePlacement();

      this.klarnaUpdateTimer = window.setTimeout(
        synchronizePlacement,
        500
      );
    }
  }

  if (!customElements.get("bt-finance-options")) {
    customElements.define(
      "bt-finance-options",
      BTFinanceOptions
    );
  }
})();